import { useCallback, useState } from "react";

import { DEFAULT_PAGE_SIZE } from "@smartrr/shared/utils/paginatedQuery";
import { useToast } from "@vendor-app/app/_sharedComponents/Toast/ToastProvider";
import { UserCancellationInterceptOutcomeInterface } from "@vendor-app/domain/UserCancellationInterceptOutcomeInterface";
import userCancellationInterceptOutcomesGateway from "@vendor-app/gateways/UserCancellationInterceptOutcomes";

interface IDateRange {
  start: Date;
  end: Date;
}

export function useUserCancellationInterceptOutcomes(selectedDates: IDateRange) {
  const { addToast } = useToast();

  const [outcomes, setOutcomes] = useState<UserCancellationInterceptOutcomeInterface[]>([]);
  const [pageNumber, setPageNumber] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const load = useCallback(
    async (pageToLoad: number, append = false) => {
      try {
        if (append) {
          setIsLoadingMore(true);
        }
        const { data, totalPages: newTotalPages } = await userCancellationInterceptOutcomesGateway.fetch(
          pageToLoad,
          DEFAULT_PAGE_SIZE,
          selectedDates
        );
        setOutcomes(prevOutcomes => (append ? [...prevOutcomes, ...data] : data));
        setTotalPages(newTotalPages);
        setPageNumber(pageToLoad);
      } catch (error) {
        addToast(error.message);
      } finally {
        if (append) {
          setIsLoadingMore(false);
        }
      }
    },
    [selectedDates, addToast]
  );

  const hasMore = pageNumber + 1 < totalPages;

  const loadMore = useCallback(() => {
    if (isLoadingMore || !hasMore) {
      return;
    }
    void load(pageNumber + 1, true);
  }, [isLoadingMore, hasMore, pageNumber, load]);

  return { outcomes, load, loadMore, hasMore, isLoadingMore };
}
